/**
 * API 适配器（写透缓存 / Write-Through Cache）
 *
 * 读取：从 localStorage 缓存读取（同步，组件接口不变）
 * 写入：同步写入 localStorage + 异步 POST 到本地服务器
 * 初始化：登录成功后由 AuthContext 调用 syncFromServer()，
 *         把服务器 CSV 数据拉到本地缓存，保证多设备一致性。
 *
 * 切换方式：将 statsRepository.js 的 import 改为 './adapters/apiAdapter'
 */

const SERVER      = '';   // 相对路径，由 CRA proxy 转发到 localhost:3001
const SESSIONS_KEY = 'bjt_sessions';
const ATTEMPTS_KEY = 'bjt_attempts';

// ── 工具 ─────────────────────────────────────────────

function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

function now() {
  return new Date().toISOString();
}

function readKey(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function writeKey(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}

// 向服务器发送请求，失败时静默（不影响本地操作）
function postServer(path, body) {
  fetch(`${SERVER}${path}`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(body),
  }).catch(() => {});
}

function patchServer(path) {
  fetch(`${SERVER}${path}`, { method: 'PATCH' }).catch(() => {});
}

// ── 用户 ID ───────────────────────────────────────────

export function getCurrentUserId() {
  try {
    const raw = localStorage.getItem('bjt_auth_session');
    const s = raw ? JSON.parse(raw) : null;
    return s?.id ?? 'local';
  } catch { return 'local'; }
}

function getCurrentUser() {
  try {
    const raw = localStorage.getItem('bjt_auth_session');
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

// ── Session ──────────────────────────────────────────

export function createSession(sessionType, meta = {}) {
  const user = getCurrentUser();
  const session = {
    id:          generateId(),
    userId:      user?.id ?? 'local',
    sessionType,
    chapterId:   meta.chapterId   ?? null,
    sectionType: meta.sectionType ?? null,
    startedAt:   now(),
    finishedAt:  null,
  };
  const sessions = readKey(SESSIONS_KEY);
  sessions.push(session);
  writeKey(SESSIONS_KEY, sessions);

  postServer('/api/sessions', session);
  return session;
}

export function finalizeSession(sessionId) {
  const sessions = readKey(SESSIONS_KEY);
  const idx = sessions.findIndex(s => s.id === sessionId);
  if (idx !== -1) {
    sessions[idx] = { ...sessions[idx], finishedAt: now() };
    writeKey(SESSIONS_KEY, sessions);
  }
  patchServer(`/api/sessions/${sessionId}`);
}

export function getRecentSessions(limit = 20) {
  const userId = getCurrentUserId();
  return readKey(SESSIONS_KEY)
    .filter(s => s.userId === userId)
    .slice(-limit)
    .reverse();
}

// ── Attempt ──────────────────────────────────────────

export function recordAttempt(sessionId, questionId, selectedOption, isCorrect) {
  const user    = getCurrentUser();
  const sessions = readKey(SESSIONS_KEY);
  const session = sessions.find(s => s.id === sessionId);

  const attempt = {
    id:             generateId(),
    sessionId,
    userId:         user?.id ?? 'local',
    questionId,
    selectedOption,
    isCorrect,
    answeredAt:     now(),
  };

  // 写入本地缓存
  const attempts = readKey(ATTEMPTS_KEY);
  attempts.push(attempt);
  writeKey(ATTEMPTS_KEY, attempts);

  // 异步写入服务器（含冗余字段，便于 CSV 直接阅读）
  postServer('/api/attempts', {
    ...attempt,
    username:    user?.username ?? '',
    sessionType: session?.sessionType ?? '',
    chapterId:   session?.chapterId   ?? null,
  });
}

export function getQuestionStats(questionIds = null) {
  const userId   = getCurrentUserId();
  const attempts = readKey(ATTEMPTS_KEY);
  const result   = {};

  attempts
    .filter(a => a.userId === userId)
    .filter(a => questionIds === null || questionIds.includes(a.questionId))
    .forEach(a => {
      if (!result[a.questionId]) result[a.questionId] = { correct: 0, total: 0 };
      result[a.questionId].total++;
      if (a.isCorrect) result[a.questionId].correct++;
    });

  return result;
}

// ── 登录后从服务器同步到本地缓存 ──────────────────────────
// 由 AuthContext 在登录/注册成功后调用。
// 确保换设备或清除浏览器后数据能从服务器 CSV 恢复。

export async function syncFromServer(userId) {
  try {
    const [attRes, sessRes] = await Promise.all([
      fetch(`${SERVER}/api/attempts?userId=${encodeURIComponent(userId)}`),
      fetch(`${SERVER}/api/sessions?userId=${encodeURIComponent(userId)}`),
    ]);
    const [attempts, sessions] = await Promise.all([attRes.json(), sessRes.json()]);
    writeKey(ATTEMPTS_KEY, attempts);
    writeKey(SESSIONS_KEY, sessions);
  } catch {
    // 服务器不可用时静默失败，继续使用本地缓存
  }
}
